/* ──────────────────────────────────────────────────────────────
   BonusA365Page  ·  Informe de bonus “Apoyo 365” por profesor
   + histórico de checkpoints
   ────────────────────────────────────────────────────────────── */
import { useState, Fragment } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
/* MUI */
import {
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Collapse,
  LinearProgress,
  Checkbox,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

/* hooks y API */
import api from '../../../api/axios';
import { useBonusA365Report } from '../../../hooks/useReportJob';
import { useProfesores } from '../../../hooks/useProfesores';
import { BonusA365Row, BonusA365ReservaRow } from '../../../api/reports';
import { getBonusCheckpoints, toggleBonusCheckpoint, BonusCheckpointRow } from '../../../api/bonus';

/* ──────────────────────────────────────────────────────────────
   Tipos auxiliares
   ────────────────────────────────────────────────────────────── */
interface DetRow extends BonusA365ReservaRow {
  horaInicio?: string;
  horaFin?: string;
}

/* ═════════════════════════════════════════════════════════════
   Helper ▸ completa durMin / acumMin y marca checkpoint
   ═════════════════════════════════════════════════════════════ */
function enrichDetalleRows(rows: DetRow[], cpSet: Set<number>): BonusA365ReservaRow[] {
  let acum = 0;

  return rows.map((r) => {
    /* duración (en minutos) */
    const dur = Number(
      r.durMin ??
        (r.horaInicio && r.horaFin
          ? dayjs(`${r.fecha}T${r.horaFin}`).diff(dayjs(`${r.fecha}T${r.horaInicio}`), 'minute')
          : 0),
    );

    /* acumulado – el reset se hace *después* de computar la fila cp */
    const isCp = cpSet.has(r.id);

    acum += dur; // ① sumamos la propia clase
    const acumForRow = acum; // ② este es el valor que verá la UI

    if (isCp) acum = 0; // ③ …y ahora sí reiniciamos para la siguiente

    return {
      ...r,
      durMin: dur,
      acumMin: acumForRow,
      checkpoint: isCp,
    };
  });
}

/* ═════════════════════════════════════════════════════════════
   Componente
   ═════════════════════════════════════════════════════════════ */
export default function BonusA365Page() {
  const { t } = useTranslation<'translation'>();
  /* — filtros — */
  const [desde, setDesde] = useState(dayjs().format('YYYY-MM-DD'));
  const [hasta, setHasta] = useState(dayjs().format('YYYY-MM-DD'));

  /* — profesor — */
  const { rows: profs } = useProfesores();
  const [profSel, setProfSel] = useState<{ id: number; nombre: string } | null>(null);

  /* — worker resumen — */
  const { run, useStatus } = useBonusA365Report();
  const [jobId, setJobId] = useState<string>();
  const { data: status } = useStatus(jobId);

  /* — detalle reservas — */
  const [openRow, setOpenRow] = useState<string>();
  const [detCache, setDet] = useState<Record<string, BonusA365ReservaRow[]>>({});
  const [loadingDet, setLoadingDet] = useState<string>();

  /* — histórico checkpoints — */
  const [chkRows, setChkRows] = useState<BonusCheckpointRow[]>([]);
  const [chkLoading, setChkLoading] = useState(false);

  /* ─── helpers ────────────────────────────────────────────── */
  /** carga/recarga y DEVUELVE las filas */
  const loadCheckpoints = async (): Promise<BonusCheckpointRow[]> => {
    if (!profSel) return [];
    setChkLoading(true);
    try {
      const rows = await getBonusCheckpoints(profSel.id, desde, hasta);
      setChkRows(rows);
      return rows;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setChkRows([]);
        return [];
      }
      throw err;
    } finally {
      setChkLoading(false);
    }
  };

  /** resumen + histórico */
  const handleGenerate = async () => {
    if (!profSel) return;

    const { jobId } = await run({ profesorId: profSel.id, desde, hasta });
    setJobId(jobId);
    setOpenRow(undefined);
    setDet({});
    await loadCheckpoints();
  };

  /** expandir detalle */
  const toggleDetalle = async (rowId: string, alumnoId: number, asignaturaId: number) => {
    if (openRow === rowId) {
      setOpenRow(undefined);
      return;
    }

    if (!detCache[rowId]) {
      setLoadingDet(rowId);
      try {
        const { data } = await api.get<DetRow[]>(
          `/bonus-a365/reservas/${profSel!.id}/${alumnoId}/${asignaturaId}`,
          { params: { desde, hasta } },
        );
        const cpSet = new Set(chkRows.map((c) => Number(c.reservaId)));
        setDet((prev) => ({
          ...prev,
          [rowId]: enrichDetalleRows(data, cpSet),
        }));
      } finally {
        setLoadingDet('');
      }
    }
    setOpenRow(rowId);
  };

  /** marcar / desmarcar checkpoint */
  const toggleCheckpoint = async (reservaId: number, alumnoId?: number) => {
    await toggleBonusCheckpoint(reservaId, alumnoId);

    /* ① refresca lista de checkpoints y obtén el set actualizado */
    const rows = await loadCheckpoints();
    const cpSet = new Set(rows.map((r) => Number(r.reservaId)));

    /* ② recarga el detalle abierto con el nuevo set */
    if (openRow) {
      const [alId, asigId] = openRow.split('-').map(Number);
      const { data } = await api.get<DetRow[]>(
        `/bonus-a365/reservas/${profSel!.id}/${alId}/${asigId}`,
        { params: { desde, hasta } },
      );
      setDet((prev) => ({
        ...prev,
        [openRow]: enrichDetalleRows(data, cpSet),
      }));
    }
  };

  /** eliminar desde el histórico */
  const deleteCheckpoint = async (reservaId: number, alumnoId: number) => {
    if (!window.confirm(t<string>('bonusA365.confirm.eliminar'))) return;
    await toggleCheckpoint(reservaId, alumnoId); // reutiliza lógica
  };

  /* — tabla resumen — */
  const resumenRows: BonusA365Row[] = status?.result?.rows ?? [];

  /* ═════════════  RENDER  ═════════════ */
  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t('bonusA365.titulo')}</Typography>

      {/* filtros */}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          type="date"
          label={t('bonusA365.filtros.desde')}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <TextField
          type="date"
          label={t('bonusA365.filtros.hasta')}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />

        <Autocomplete
          options={profs}
          getOptionLabel={(o) => o.nombre}
          value={profSel}
          onChange={(_, v) => setProfSel(v)}
          sx={{ minWidth: 260 }}
          renderInput={(p) => (
            <TextField
              {...p}
              size="small"
              placeholder={t<string>('bonusA365.filtros.profesorPlaceholder')}
              InputProps={{
                ...p.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Button variant="contained" disabled={!profSel} onClick={handleGenerate}>
          {t('bonusA365.filtros.generar')}
        </Button>
      </Stack>

      {/* progreso */}
      {status && !status.done && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="body2">
            {t('bonusA365.estado.generando', { progress: status.progress })}
          </Typography>
        </Stack>
      )}

      {/* resumen */}
      {status?.done && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>
                  <b>{t('bonusA365.resumen.alumno')}</b>
                </TableCell>
                <TableCell>
                  <b>{t('bonusA365.resumen.asignatura')}</b>
                </TableCell>
                <TableCell align="right">
                  <b>{t('bonusA365.resumen.horas')}</b>
                </TableCell>
                <TableCell align="right">
                  <b>{t('bonusA365.resumen.ultimaClase')}</b>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {resumenRows.map((r) => {
                const rowId = `${r.alumnoId}-${r.asignaturaId}`;
                const det = detCache[rowId] ?? [];

                return (
                  <Fragment key={rowId}>
                    {/* fila resumen */}
                    <TableRow hover selected={openRow === rowId}>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggleDetalle(rowId, r.alumnoId, r.asignaturaId)}
                        >
                          {openRow === rowId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{r.alumno}</TableCell>
                      <TableCell>{r.asignatura}</TableCell>
                      <TableCell align="right">{r.horas.toFixed(1)} h</TableCell>
                      <TableCell align="right">
                        {dayjs(r.ultimaClase).format('YYYY-MM-DD')}
                      </TableCell>
                    </TableRow>

                    {/* detalle */}
                    <TableRow>
                      <TableCell colSpan={6} sx={{ p: 0 }}>
                        <Collapse in={openRow === rowId} unmountOnExit>
                          <Paper variant="outlined" sx={{ m: 1 }}>
                            {loadingDet === rowId && (
                              <Stack alignItems="center" py={3}>
                                <CircularProgress />
                              </Stack>
                            )}

                            {!loadingDet && (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>{t('bonusA365.detalle.fecha')}</TableCell>
                                    <TableCell align="right">
                                      {t('bonusA365.detalle.duracion')}
                                    </TableCell>
                                    <TableCell align="right">
                                      {t('bonusA365.detalle.acumulado')}
                                    </TableCell>
                                    <TableCell align="center">
                                      {t('bonusA365.detalle.checkpoint')}
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {det.map((d) => (
                                    <TableRow key={d.id}>
                                      <TableCell>{d.fecha}</TableCell>
                                      <TableCell align="right">
                                        {(d.durMin / 60).toFixed(1)} h
                                      </TableCell>
                                      <TableCell align="right">
                                        {(d.acumMin / 60).toFixed(1)} h
                                      </TableCell>
                                      <TableCell align="center">
                                        <Checkbox
                                          checked={d.checkpoint}
                                          icon={<DoneAllIcon fontSize="small" />}
                                          checkedIcon={
                                            <DoneAllIcon color="success" fontSize="small" />
                                          }
                                          onChange={() => toggleCheckpoint(d.id, r.alumnoId)}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Paper>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* histórico */}
      {profSel && (
        <Paper sx={{ mt: 4 }}>
          <Typography variant="subtitle1" sx={{ px: 2, pt: 2 }}>
            {t('bonusA365.historial.titulo')}
          </Typography>

          {chkLoading ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress />
            </Stack>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('bonusA365.historial.fechaClase')}</TableCell>
                  <TableCell>{t('bonusA365.historial.alumno')}</TableCell>
                  <TableCell>{t('bonusA365.historial.asignatura')}</TableCell>
                  <TableCell align="center">{t('bonusA365.historial.reserva')}</TableCell>
                  <TableCell>{t('bonusA365.historial.marcadoPor')}</TableCell>
                  <TableCell>{t('bonusA365.historial.fechaMarcado')}</TableCell>
                  <TableCell align="center">{t('bonusA365.historial.acciones')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {chkRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                      {t('bonusA365.historial.vacio')}
                    </TableCell>
                  </TableRow>
                )}

                {chkRows.map((c) => (
                  <TableRow key={`${c.reservaId}-${c.alumnoId}-${c.asignaturaId}`}>
                    <TableCell>{c.fecha}</TableCell>
                    <TableCell>{c.alumno}</TableCell>
                    <TableCell>{c.asignatura}</TableCell>
                    <TableCell align="center">#{c.reservaId}</TableCell>
                    <TableCell>{c.marcadoPor}</TableCell>
                    <TableCell>{c.fechaMarcado}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteCheckpoint(c.reservaId, c.alumnoId)}
                      >
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}
    </Stack>
  );
}
